import { needsNoLocalePrefix } from '@/i18n/locale-prefix'
import { locales, type Locale } from '@/i18n/locales'

import { localeHomeUrl } from './menu-config'

/**
 * The country picker's options — which countries the header offers, and where each
 * one sends the visitor from the page they are on.
 *
 * **Country, not language.** The control is a flag and a place name because that is
 * what a visitor is choosing: the market they are shopping in (routes, carriers,
 * currency), of which the language is a consequence. It is keyed by locale because a
 * locale is what nexus routes on, and today the two are 1:1 — the shape carries no
 * multi-locale country (ui-react's `Canada EN FR`), because nexus serves none.
 * Adding one means a country with several locales, not another entry here.
 *
 * Country names are **endonyms** — each country's name in its own language, so
 * `México` and `United States` read the same whichever locale is active. They are
 * proper nouns, so they stay out of the translation store for the same reason
 * carrier names do (`menu-config.ts`, RD-5): a translator handed "United States"
 * would be asked to translate a name, not prose. ui-react's `countries.json` is the
 * surface this mirrors.
 */
interface Country {
    /** ISO 3166-1 alpha-2, lowercased — also the flag asset's basename. */
    countryCode: string
    /** The country's name in its own language. Never translated. */
    countryName: string
    /** The locale nexus serves this country in. */
    locale: Locale
}

const COUNTRIES: readonly Country[] = [
    { countryCode: 'mx', countryName: 'México', locale: 'es-mx' },
    { countryCode: 'us', countryName: 'United States', locale: 'en-us' },
]

/**
 * The 4x3 flag for a country. Sourced from `flag-icons` (MIT) via ui-react's
 * `wanderu-flag-icons`, so the picker shows the same artwork as the legacy header —
 * see `LICENSE.third-party.md`.
 */
export function flagSrc(countryCode: string): string {
    return `/next-static/flags/${countryCode}.svg`
}

/** A country as the picker renders it: the flag, the name, and where it goes. */
export interface LocaleOption extends Country {
    /** Flag asset path (`flagSrc`). */
    flag: string
    /** The destination — a final URL, never one that redirects. See `localeHref`. */
    href: string
    /** Whether this is the country the visitor is already in. */
    isActive: boolean
}

/**
 * First path segment of a locale-less path, or `''` — `/bus/us-ny/…` → `'bus'`.
 * Whole-segment matching keeps `/world-cup-2026-recap/` out of `world-cup-2026`.
 */
function baseSegment(pathname: string): string {
    return pathname.split(/[?#]/)[0].split('/').find(Boolean) ?? ''
}

/**
 * Routes this app serves in **en-us only, and `404`s elsewhere** — the page itself
 * calls `notFound()` for any other locale, and `src/proxy.ts` does *not* coerce the
 * base path, so nothing rescues the request.
 *
 * They are why the picker cannot simply prefix every path. A single-locale route the
 * proxy *does* coerce (`about-us`, `partners`, `privacy-policy`, `terms-and-conditions`)
 * is a sanctioned carve-out in `invariants.md#rendered-links-are-canonical` — it `307`s
 * the reader onto a working page. These do not: they answer `404`, and the same rule
 * says plainly that **a `404` is never sanctioned**. So the picker sends them to the
 * target locale's home, exactly as it does a locale-free surface.
 *
 * **Adding an en-us-only page means adding it here** — the drift guard in
 * `locale-menu-config.test.ts` walks the route tree for the `notFound()`-on-locale
 * shape and fails on any base path that neither this list nor the proxy covers, so
 * the omission is a failing build rather than a `404` nobody notices.
 */
const EN_US_ONLY_SEGMENTS = new Set(['70mm-imax-theaters', 'airport-shuttles', 'world-cup-2026'])

/**
 * Where picking `locale` sends a visitor who is on `pathname` (the **locale-less**
 * path, as `usePathname` from `@/i18n/navigation` reports it).
 *
 * Same path in the target locale, so the visitor keeps their place — with three
 * exceptions, all of which would otherwise emit a href that does not answer `200`
 * (`invariants.md#rendered-links-are-canonical`), and all decidable from the path
 * alone:
 *
 * - the **homepage**, whose default-locale form is the bare `/` (`/en-us/` `301`s);
 * - a **locale-free surface** (`/blog/…`, `/social`) — one URL, no per-locale
 *   variant, so prefixing `301`s straight back and *not* prefixing would point every
 *   country at the identical page. Neither is a locale switch;
 * - an **en-us-only route** that `404`s in any other locale (`EN_US_ONLY_SEGMENTS`).
 *
 * Each falls back to the target locale's home — the nearest page that genuinely
 * differs by locale. Going through `localeHomeUrl` keeps this in step with the brand
 * logo, which answers the same question.
 */
export function localeHref(locale: Locale, pathname: string): string {
    if (
        pathname === '/' ||
        needsNoLocalePrefix(pathname) ||
        EN_US_ONLY_SEGMENTS.has(baseSegment(pathname))
    ) {
        return localeHomeUrl(locale)
    }
    return `/${locale}${pathname}`
}

/**
 * The picker's options for a visitor in `activeLocale` on `pathname`, ordered by
 * country name — ui-react sorts the same way (`localeCompare(_, 'en')`), so the two
 * headers list the same countries in the same order.
 *
 * React-free like the rest of this directory's config, so it unit-tests directly.
 */
export function buildLocaleOptions(activeLocale: Locale, pathname: string): LocaleOption[] {
    return COUNTRIES.filter((country) => locales.includes(country.locale))
        .map((country) => ({
            ...country,
            flag: flagSrc(country.countryCode),
            href: localeHref(country.locale, pathname),
            isActive: country.locale === activeLocale,
        }))
        .sort((a, b) => a.countryName.localeCompare(b.countryName, 'en'))
}

/**
 * The option the trigger shows. Total by construction — `activeLocale` is a served
 * locale and every served locale has a country — but a caller still gets `undefined`
 * rather than a throw if that ever stops holding, so the header degrades to no
 * picker instead of a blank page.
 */
export function activeLocaleOption(options: LocaleOption[]): LocaleOption | undefined {
    return options.find((option) => option.isActive)
}
